import React, { createContext, useState, useContext } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { IconButton, SxProps, Theme } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { SlMenu, useStacklineTheme } from '@stackline/ui';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { useQuery } from '@tanstack/react-query';
import LegendWithMetric, { LegendWithMetricProps } from 'src/components/BeaconV2Components/Legend/LegendWithMetric';
import { useQueryKey } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useQueryKey';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { useCsvExporter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useCsvExporter';

const BeaconProChartContext = createContext({
  chartName: '',
  chartContainerRef: null,
  data: null,
  isLoading: true
});

/**
 * BeaconProChart is a compound component used to render visualizations with arbitrary data fetching and rendering logic.
 * The main goal is to provide components that share some state and logic, such as a dropdown to change the data displayed, that can be composed together.
 */
export default function BeaconProChart({
  name,
  children,
  queryFn
}: {
  name: string;
  children: React.ReactNode;
  queryFn: () => Promise<unknown>;
}) {
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const queryKey = useQueryKey(name);

  const { data, isLoading } = useQuery({
    queryKey: [queryKey],
    queryFn,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  return (
    <div style={{ backgroundColor: 'white' }} ref={chartContainerRef}>
      <BeaconProChartContext.Provider
        value={{
          chartContainerRef,
          chartName: name,
          isLoading,
          data
        }}
      >
        <Flex flexDirection="column">{children}</Flex>
      </BeaconProChartContext.Provider>
    </div>
  );
}

export interface OptionProps {
  id: string;
  label: string;
  queryFn: () => Promise<unknown>;
}

/**
 * Default button that opens a context menu to export chart visualization/data
 */
export function DefaultMenuButton({ excludeActions = [] }: { excludeActions?: string[] }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data, chartName, chartContainerRef } = useContext(BeaconProChartContext);
  const theme = useStacklineTheme();

  const { saveImage, copyImage, exportCsv } = useCsvExporter(chartName, chartContainerRef);

  const dropdownOptions: (SlDropdownMenuOption & { onClick: () => void })[] = [
    {
      id: 'saveImage',
      label: 'Save image',
      onClick: saveImage
    },
    {
      id: 'copyImage',
      label: 'Copy image',
      onClick: copyImage
    },
    {
      id: 'exportCsv',
      label: 'Export CSV',
      onClick: () => exportCsv(data)
    }
  ];

  const filteredDropdownOptions = dropdownOptions.filter((opt) => !excludeActions.includes(opt.id));

  return (
    <div>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          height: theme.spacing.mdl,
          width: theme.spacing.mdl,
          borderRadius: theme.spacing.xs,
          color: theme.colors.primary
        }}
      >
        <MoreHorizIcon />
      </IconButton>
      <SlMenu
        anchorEl={anchorEl}
        open={!!anchorEl}
        selectedId={null}
        onChange={(e) => {
          const onClick =
            filteredDropdownOptions.find((opt) => opt.id === e.id)?.onClick ??
            (() => {
              console.warn('No onClick handler for option', e);
            });

          onClick();
          setAnchorEl(null);
        }}
        onClose={() => setAnchorEl(null)}
        options={filteredDropdownOptions}
      />
    </div>
  );
}

BeaconProChart.Header = function BeaconProChartHeader({
  children,
  menuButton = <DefaultMenuButton excludeActions={[]} />,
  sx
}: {
  children: React.ReactNode;
  menuButton?: React.ReactNode | ((data: any, isLoading: boolean) => React.ReactNode);
  sx?: SxProps<Theme>;
}) {
  const { data, isLoading } = useContext(BeaconProChartContext);
  return (
    <Flex justifyContent="space-between" alignItems="center" sx={sx}>
      {children}
      {typeof menuButton === 'function' ? menuButton(data, isLoading) : menuButton}
    </Flex>
  );
};

/**
 * Title component of the chart, which can be a simple title or a dropdown title.
 *
 * @param title: title of the chart
 * @param prefix: prefix used in the case of dropdown
 * @param menuButton: overrides the default export menu button
 *
 * @returns a simple title or a dropdown title depending on the number of options registered
 */
BeaconProChart.Title = function BeaconProChartTitle({ children }: { children: React.ReactNode }) {
  return (
    <Flex alignItems="center" gap={6}>
      {children}
    </Flex>
  );
};

BeaconProChart.Subtitle = function BeaconProChartSubtitle({
  children,
  render,
  renderLoading = () => <SlSkeleton variant="rounded" height={20} width="100%" sx={{ marginTop: '10px' }} />
}: {
  children?: React.ReactNode;
  render?: (data: any, isLoading: boolean) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
}) {
  const { data, isLoading } = React.useContext(BeaconProChartContext);

  if (isLoading) {
    return renderLoading();
  }

  const subtitle = render ? render(data, isLoading) : children;
  return (
    <Text variant="body1" sx={{ marginTop: '8px' }}>
      {subtitle}
    </Text>
  );
};

interface LegendProps {
  primaryLegendProps?: Omit<LegendWithMetricProps, 'color'>;
  comparisonLegendProps?: Omit<LegendWithMetricProps, 'color'>;
  render?: (data: any) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
}

/**
 * Legend component of the chart, which can be rendered in a custom way or using the default LegendWithMetric component.
 *
 * @param render (optional): custom render call backfunction for the legend. The data will be passed as an argument. This allows the caller to render the legend in a custom way based off of the chart data.
 * @param primaryLegendProps or comparisonLegendProps: if render is not supplised, these props will be used to render a LegendWithMetric component
 *
 * @returns custom render or default LegendWithMetric component
 */
BeaconProChart.Legend = function BeaconProChartLegend({
  primaryLegendProps,
  comparisonLegendProps,
  render,
  renderLoading = () => {
    return (
      <Flex gap={32} marginY="32px">
        <Flex flexDirection="column" gap={8}>
          <SlSkeleton variant="rounded" height={28} width={68} />
          <SlSkeleton variant="rounded" height={20} width={180} />
        </Flex>
        <Flex flexDirection="column" gap={8}>
          <SlSkeleton variant="rounded" height={28} width={68} />
          <SlSkeleton variant="rounded" height={20} width={180} />
        </Flex>
      </Flex>
    );
  }
}: LegendProps) {
  // TODO(tyan): add rendering/skeleton logic for isLoading
  const { data, isLoading } = React.useContext(BeaconProChartContext);

  if (isLoading) {
    return renderLoading();
  }

  if (render && data) {
    return render(data);
  }

  return (
    <Flex gap="lg" sx={{ marginTop: '32px' }}>
      {comparisonLegendProps && <LegendWithMetric color="casper" {...comparisonLegendProps} />}
      {primaryLegendProps && <LegendWithMetric color="primary" {...primaryLegendProps} />}
    </Flex>
  );
};

interface VisualizationProps {
  render: (data: unknown, isLoading: boolean) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
  deferLoading?: boolean; // If true, passes isLoading to render function instead of showing loading state
}

/**
 * The main visualization component of the chart, which will render the chart based on the data fetched.
 *
 * @param render: custom render call backfunction for the legend. The data will be passed as an argument. This allows the caller to render the chart in a custom way based off of the chart data.
 * @param deferLoading: if true, passes isLoading to render function instead of showing loading state
 *
 * @returns custom rendered chart defined by the caller
 */
BeaconProChart.Visualization = function BeaconProChartVisualization({
  render,
  renderLoading = () => <SlSkeleton variant="rounded" height={280} width="100%" />,
  deferLoading = false
}: VisualizationProps) {
  const { data, isLoading } = React.useContext(BeaconProChartContext);

  if (isLoading && !deferLoading) {
    return renderLoading();
  }

  return render(data, isLoading);
};
